import {Component,forwardRef,Input,OnInit} from '@angular/core';
import {ControlValueAccessor,NG_VALUE_ACCESSOR} from '@angular/forms';

@Component({
    selector: 'tinymce-input',
    templateUrl: './tinymce-input.component.html',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => TinymceInputComponent),
        multi: true
    }]
})
export class TinymceInputComponent implements OnInit, ControlValueAccessor {

    /** Libellé du champ */
    @Input() libelle: string;

     /** Champ obligatoire */
    @Input() required: boolean = false;

     /** Configuration TinyMCE personnalisée */
    @Input() tinymceConfig: any = {};

     /** Valeur du champ */
    value: string = '';

    /** Indique si TinyMCE est chargé et si l’éditeur peut être affiché */
     isPret: boolean = false;

     /** Callbacks ControlValueAccessor */
    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

     /** Configuration TinyMCE par défaut */
    defaultConfig: any = {
        base_url: '/tinymce',
        suffix: '.min',
        height: 250,
        menubar: false,
        statusbar: false,
        promotion: false,
        branding: false,
        convert_urls: false,
        remove_script_host: false,
        language: 'fr_FR',
        plugins: 'advlist lists charmap preview anchor pagebreak wordcount code fullscreen insertdatetime media nonbreaking table directionality',
        toolbar1: 'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent blockquote | superscript subscript',
        toolbar2: 'preview | forecolor backcolor | table | blocks fontfamily fontsize'
    };

    /** Configuration finale (merge default + custom) */
    finalConfig: any;

    async ngOnInit(): Promise<void> {
        //Charger TinyMCE
        try {
            await this.loadTinyMCE();
        } catch (error) {
            console.error('Erreur chargement TinyMCE:', error);
        }

        //Fusionner config par défaut et config personnalisée
        this.finalConfig = {...this.defaultConfig, ...this.tinymceConfig};
        this.isPret = true;
    }

    /**
     * Charge le script TinyMCE en local si pas déjà présent
     */
    private async loadTinyMCE(): Promise<void> {
        if ((window as any).tinymce) {
            return;
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/tinymce/tinymce.min.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Impossible de charger TinyMCE'));
            document.head.appendChild(script);
        });
    }

    /**
     * Détermine si le contenu est vide
     */
    public isVide(): boolean {
        const html = this.value || '';
        const sansHtml = html.replace(/<[^>]*>/g, '');
        const texte = sansHtml.replace(/&nbsp;/gi, ' ').trim();
        return texte.length === 0;
    }

    /**
     * Permet au composant d’être utilisé comme un champ Angular.
     */
    writeValue(value: any): void {
        //Reçoit la valeur depuis le formulaire parent
        this.value = value || '';
    }

    /**
     * Enregistre le callback appelé par Angular quand la valeur du champ change.
     *
     * @param fn Fonction fournie par Angular Forms
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Enregistre le callback appelé par Angular quand le champ est “touché”.
     *
     * @param fn Fonction fournie par Angular Forms
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Appelé quand le contenu de l’éditeur change.
     * Met à jour la valeur interne et propage le changement au formulaire parent.
     *
     * @param newValue Nouveau contenu HTML de l’éditeur
     */
    onModelChange(newValue: string): void {
        this.value = newValue;
        this.onChange(newValue);
        this.onTouched();
    }
}